// import { useExternalTask } from "@/hooks";
import { DocumentController } from "@services/common/services/api/controllers";
import {
  Button,
  LoadingOverlay,
  onValidateFiles,
  showNotification,
} from "@farzoom/common-ui-components";
import { Empty } from "antd";
import classnames from "classnames";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { Sign } from "../Sign/Sign";
import { checkSigned } from "../Sign/utils/fileSignUtils";
import { FileItem } from "./components/fileItem";
import { useStyles } from "./styles";
import { validateFiles } from "./utils/validateFiles";

const ERROR_CLASSNAME = "ant-form-item-has-error";

export const Document = ({
  prefix,
  config,
  value,
  update,
  readonly,
  allowedTypes,
  uploadMethod,
  userInfo,
  isSingleSign,
  taskFormSignUpdate,
}) => {

  const {control} = useFormContext();
  const classes = useStyles();
  const inputRef = useRef();
  const dropHolderRef = useRef();
  // const { token } = useExternalTask();
  const currentUserCompanyInn = userInfo?.company?.inn;
  const {
    fieldState: { invalid, error },
  } = useController({
    control,
    name: prefix,
    rules: {
      validate: () => validateFiles(value?.files || [], config, readonly, currentUserCompanyInn),
    },
  });

  const [filesToUpload, setFilesToUpload] = useState([]);
  const [immutableFiles, setImmutableFiles] = useState({});
  const [isBusy, setIsBusy] = useState(false);

  const isSigned = value?.files.length
    ? value?.files.every((file) => checkSigned(file, config.needCoSign, currentUserCompanyInn))
    : false;

  const isError = !!invalid && (!value?.files?.length || (config?.sign && !isSigned));
  const [isDragged, setIsDragged] = useState(false);

  const handleFiles = function () {
    const files = onValidateFiles({
      files: this.files,
      allowedTypes,
      onError: (message) => {
        showNotification({
          message,
          type: "error",
          duration: 20,
        });
      },
    });
    setFilesToUpload(files);
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.addEventListener("change", handleFiles, false);
    }

    setImmutableFiles(
      Object.assign({}, ...(value?.files || []).map((f) => ({ [f.fileId]: true })))
    );
  }, []);

  const uploadFile = (file) => {
    const data = new FormData();
    data.append("file", file);
    return uploadMethod ? uploadMethod(data) : DocumentController.upload(data, undefined);
  };

  useEffect(() => {
    const onError = (data) => {
      showNotification({
        type: "error",
        message:
          data?.response?.status === 413
            ? "Файл превышает максимально допустимый размер"
            : "Ошибка загрузки файла",
      });
      const files = value?.files || [];
      update({
        ...value,
        files: [...files],
        docType: config.docType,
      });
      inputRef.current.value = null;
      setFilesToUpload([]);
      setIsBusy(false);
    };
    if (filesToUpload.length > 0 && !isBusy) {
      setIsBusy(true);
      const uploadList = [];
      for (let i = 0; i < filesToUpload.length; i++) {
        uploadList.push(uploadFile(filesToUpload[i]));
      }

      Promise.all(uploadList)
        .then((response) => {
          const files = value?.files || [];
          const uploadedList = response.reduce((result, item) => {
            if (item?.request?.status === 200) {
              result.push(item.data);
            }
            return result;
          }, []);
          if (uploadedList?.length > 0) {
            update({
              ...value,
              files: config?.multiple ? [...files, ...uploadedList] : [...uploadedList],
              docType: config.docType,
            });
            inputRef.current.value = null;
            setFilesToUpload([]);
            setIsBusy(false);
          } else if (uploadMethod) {
            setIsBusy(false);
          } else {
            onError(response[0]);
          }
        })
        .catch((err) => {
          if (uploadMethod) {
            setIsBusy(false);
          }
          if (!uploadMethod) {
            onError(err);
          }
        });
    }
  }, [filesToUpload]);

  const onUpload = () => inputRef.current.click();

  const onRemove = (fileId) => () => {
    const files = value?.files || [];
    update({
      ...value,
      files: files.filter((file) => file.fileId !== fileId),
      docType: config.docType,
    });
  };

  const [isSignInfoOpen, setIsSignInfoOpen] = useState({});

  const toggleSignInfo = (fileId) => () => {
    setIsSignInfoOpen((isSignInfoOpen) => ({
      ...isSignInfoOpen,
      [fileId]: !isSignInfoOpen[fileId],
    }));
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (!config?.load) return;

    if (!config.multiple) {
      const dt = new DataTransfer();
      dt.items.add(e.dataTransfer.files[0]);

      handleFiles.apply({ files: dt.files });
      setIsDragged(false);
      return;
    }

    handleFiles.apply({ files: e.dataTransfer.files });
    setIsDragged(false);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (!config?.load) return;

    setIsDragged(true);
  };

  return (
    <>
      {(((!readonly || !config?.readonly) && config?.load) || value?.files?.length) && (
        <>
          <div
            ref={dropHolderRef}
            className={classnames(classes.document, { isError, isDragged })}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={() => setIsDragged(false)}
          >
            {isBusy && <LoadingOverlay text="Обработка" />}
            {!readonly && config?.load && (
              <Button iconName="add" className={classes.buttonAdd} onClick={onUpload} />
            )}
            <input
              ref={inputRef}
              className={classes.fileInput}
              type="file"
              multiple={config?.multiple}
            />
            <div className={classes.wrapper}>
              <div className={classes.title}>{config?.title}</div>
              {value?.files?.length > 0 && (
                <div className={classes.files}>
                  {value?.files?.map((file) => (
                    <FileItem
                      key={file.fileId}
                      file={file}
                      canRemoveFile={
                        !readonly &&
                        ((config?.load && !immutableFiles[file.fileId]) || config?.remove)
                      }
                      onRemove={onRemove}
                      toggleSignInfo={toggleSignInfo}
                      isSignInfoOpen={isSignInfoOpen}
                    />
                  ))}
                  {!!readonly && !value?.files?.length && (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Загруженные файлы отсутствуют"
                    />
                  )}
                </div>
              )}
            </div>
            {config?.sign && (
              <Sign
                config={config}
                value={value}
                update={update}
                isSuccess={isSigned}
                needCoSign={config.needCoSign}
                userInfo={userInfo}
                showSignBtn={isSingleSign}
                taskFormSignUpdate={taskFormSignUpdate}
              />
            )}
          </div>
          {isError && (
            <div className={classnames(classes.error, ERROR_CLASSNAME)}>{error?.message}</div>
          )}
        </>
      )}
    </>
  );
};

Document.propTypes = {
  control: PropTypes.object,
  prefix: PropTypes.string,
  config: PropTypes.object,
  value: PropTypes.object,
  update: PropTypes.func,
  isTest: PropTypes.bool,
  isDownload: PropTypes.bool,
  readonly: PropTypes.bool,
  isSingleSign: PropTypes.bool,
};
